import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import salesService from '../services/salesService';
import refundsService from '../services/refundsService';
import { getReceiptCandidates, normalizeReceiptNumber } from '../utils/receiptUtils';

const RefundPage = () => {
  const { receiptNumber } = useParams();
  const [sale, setSale] = useState(null);
  const [itemsToReturn, setItemsToReturn] = useState([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchSale = async () => {
      const normalized = normalizeReceiptNumber(receiptNumber) || receiptNumber;
      const candidates = getReceiptCandidates(normalized || receiptNumber);
      let lastError = null;
      for (const candidate of candidates) {
        try {
          const saleData = await salesService.getSaleByReceipt(candidate);
          setSale(saleData);
          return;
        } catch (err) {
          lastError = err;
        }
      }
      setError(lastError?.response?.data?.message || 'Failed to fetch sale details.');
    };
    fetchSale();
  }, [receiptNumber]);

  const handleCheckboxChange = (e, item) => {
    if (e.target.checked) {
      setItemsToReturn([...itemsToReturn, { productId: item.product, quantity: item.quantity }]);
    } else {
      setItemsToReturn(itemsToReturn.filter(i => i.productId !== item.product));
    }
  };

  const handleProcessRefund = async () => {
    try {
      await refundsService.processRefund({
        saleId: sale._id,
        itemsToReturn,
        reason,
        storeId: sale.store._id,
      });
      setSuccess('Refund processed successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process refund.');
    }
  };

  if (!sale) {
    return <div>Loading sale details...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Process Refund</h1>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4">Sale Details</h2>
        <p><strong>Receipt Number:</strong> {sale.receiptNumber}</p>
        <p><strong>Total:</strong> ${sale.finalAmount.toFixed(2)}</p>

        <h3 className="text-lg font-bold mt-6 mb-4">Items to Refund</h3>
        <div className="space-y-4">
          {sale.items.map(item => (
            <div key={item.product} className="flex items-center">
              <input
                type="checkbox"
                onChange={(e) => handleCheckboxChange(e, item)}
                className="mr-4"
              />
              <span>{item.name} - ${item.price.toFixed(2)} x {item.quantity}</span>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
            Reason for Refund
          </label>
          <input
            type="text"
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 p-2 border rounded-md w-full"
          />
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={handleProcessRefund}
            disabled={itemsToReturn.length === 0}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark disabled:bg-gray-400"
          >
            Process Refund
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;
